import React from "react";
import SearchableDropDown from "../../../../components/SearchableDropDown";
import { STATE_OPTIONS } from "../../UploadDetailsFields";
import "./index.scss";

/**
 * GSTEntries
 *
 * Renders dynamic GST registration entries matching the reference image:
 *  - Circular blue icon badges next to column headers
 *  - 4-column layout: GST Number, State, GST Address, GST Certificate
 *  - Uploaded certificate displayed in a light-green pill with checkmark and 'View' button on the last side
 *  - Dashed cloud upload input below the uploaded file
 *  - Solid blue pill '+ Add GST' button below the cards
 */
const GSTEntries = ({
  isGSTRegistered,
  gstEntries,
  addGstEntry,
  removeGstEntry,
  handleGstChange,
  handleGstBlur,
  gstFiles,
  handleGstFileChange,
  gstErrors = {},
  existingGstFiles = [],
  gstCertErrors = {},
  onGstFileChange,
  deficiencyMap = {},
  isDeficiencyMode = false,
  onViewDocument,
  isDocUploading,
  uploadingDocKeys,
  readOnly = false,
}) => {
  if (isGSTRegistered !== "yes") return null;

  return (
    <div className="gst-entries">
      {gstEntries.map((entry, index) => {
        const certKey = `gst_file_${index}`;
        const isCertUploading = isDocUploading
          ? isDocUploading(certKey)
          : uploadingDocKeys instanceof Set
            ? uploadingDocKeys.has(certKey)
            : false;
        const isDeficient = !!deficiencyMap[certKey];
        const uploadable = !isDeficiencyMode || isDeficient;
        const existingFile = existingGstFiles[index] || null;
        const newFile = gstFiles[index] || null;
        const certError = gstCertErrors[`gst_certificate_${index}`] || "";
        const gstNumError = gstErrors[index] || "";

        return (
          <div
            key={index}
            className={`gst-entry-card ${isDeficient ? "gst-entry-card--deficient" : ""}`}
          >
            {/* Column 1: GST Number */}
            <div className="gst-col">
              <label className="gst-col__label" htmlFor={`gstNumber_${index}`}>
                <span className="gst-col__icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                    <line x1="9" y1="22" x2="9" y2="22.01" />
                    <line x1="15" y1="22" x2="15" y2="22.01" />
                    <line x1="9" y1="18" x2="9" y2="18.01" />
                    <line x1="15" y1="18" x2="15" y2="18.01" />
                    <line x1="9" y1="14" x2="9" y2="14.01" />
                    <line x1="15" y1="14" x2="15" y2="14.01" />
                    <line x1="9" y1="10" x2="9" y2="10.01" />
                    <line x1="15" y1="10" x2="15" y2="10.01" />
                    <line x1="9" y1="6" x2="9" y2="6.01" />
                    <line x1="15" y1="6" x2="15" y2="6.01" />
                  </svg>
                </span>
                <span className="gst-col__title">GST Number</span>
                <span className="required-mark"> *</span>
              </label>
              <div className="gst-input-wrapper">
                <input
                  id={`gstNumber_${index}`}
                  name={`gstNumber_${index}`}
                  type="text"
                  className={`gst-input ${gstNumError ? "gst-input--error" : ""}`}
                  placeholder="e.g. 07QAHFT6804B1Z"
                  value={entry.gstNumber || ""}
                  maxLength={15}
                  onChange={(e) =>
                    handleGstChange &&
                    handleGstChange(
                      index,
                      "gstNumber",
                      e.target.value.toUpperCase(),
                    )
                  }
                  onBlur={() => handleGstBlur && handleGstBlur(index)}
                />
              </div>
              {gstNumError && (
                <span className="input-error-msg">{gstNumError}</span>
              )}
            </div>

            {/* Column 2: State */}
            <div className="gst-col">
              <label className="gst-col__label" htmlFor={`gstState_${index}`}>
                <span className="gst-col__icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
                <span className="gst-col__title">State</span>
                <span className="required-mark"> *</span>
              </label>
              <div className="gst-input-wrapper">
                <SearchableDropDown
                  id={`gstState_${index}`}
                  name={`gstState_${index}`}
                  value={entry.gstState || entry.state || ""}
                  options={STATE_OPTIONS}
                  placeholder="Select state"
                  disabled={readOnly}
                  onChange={(e) =>
                    handleGstChange &&
                    handleGstChange(index, "gstState", e.target.value)
                  }
                  fullWidth
                />
              </div>
            </div>

            {/* Column 3: GST Address */}
            <div className="gst-col">
              <label className="gst-col__label" htmlFor={`gstAddress_${index}`}>
                <span className="gst-col__icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </span>
                <span className="gst-col__title">GST Address</span>
              </label>
              <div className="gst-input-wrapper">
                <input
                  id={`gstAddress_${index}`}
                  name={`gstAddress_${index}`}
                  type="text"
                  className="gst-input"
                  placeholder="Enter GST address"
                  value={entry.gstAddress || ""}
                  onChange={(e) =>
                    handleGstChange &&
                    handleGstChange(index, "gstAddress", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Column 4: GST Certificate */}
            <div
              className={`gst-col gst-col--cert ${isDeficient ? "doc-deficient-wrap" : ""}`}
            >
              <label className="gst-col__label">
                <span className="gst-col__icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <circle cx="9" cy="10" r="2" />
                    <line x1="15" y1="8" x2="17" y2="8" />
                    <line x1="15" y1="12" x2="17" y2="12" />
                    <line x1="7" y1="16" x2="17" y2="16" />
                  </svg>
                </span>
                <span className="gst-col__title">GST Certificate</span>
                <span className="required-mark"> *</span>
              </label>

              {/* Uploaded Certificate Green Badge (if existing or newly chosen) */}
              {(existingFile || newFile) && (
                <div className="gst-cert-uploaded">
                  <span className="gst-cert-uploaded__icon">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#16a34a"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path
                        d="M9 12l2 2 4-4"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </span>
                  <span
                    className="gst-cert-uploaded__name"
                    title={newFile ? newFile.name : existingFile?.fileName}
                  >
                    {newFile ? newFile.name : existingFile?.fileName}
                  </span>
                  {(existingFile?.filePath || newFile) && onViewDocument && (
                    <button
                      type="button"
                      className="gst-cert-uploaded__view"
                      onClick={() => {
                        if (existingFile?.filePath) {
                          onViewDocument(
                            existingFile.filePath,
                            `GST Certificate (${entry.gstNumber || index + 1})`,
                          );
                        } else if (newFile) {
                          const blobUrl = URL.createObjectURL(newFile);
                          onViewDocument(blobUrl, newFile.name);
                        }
                      }}
                      title="View GST certificate"
                    >
                      View
                    </button>
                  )}
                </div>
              )}

              {/* Dashed Upload Box */}
              <div
                className={`gst-cert-upload-box ${certError ? "gst-cert-upload-box--error" : ""}${isCertUploading ? " is-uploading" : ""}`}
              >
                <input
                  id={`gstCertificate_${index}`}
                  type="file"
                  name={`gstCertificate_${index}`}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="gst-cert-input"
                  disabled={!uploadable || isCertUploading}
                  onChange={(e) => {
                    if (!uploadable || isCertUploading) return;
                    const file = e.target.files[0];
                    if (!file) return;
                    handleGstFileChange(index, file);
                    if (onGstFileChange) onGstFileChange(index, file);
                  }}
                />
                <div className="gst-cert-box-content">
                  {isCertUploading ? (
                    <>
                      <span className="gst-upload-spinner" />
                      <span className="gst-upload-uploading-text">
                        Uploading GST certificate... please wait
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="gst-cloud-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#2563eb"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M16 16l-4-4-4 4" />
                        <path d="M12 12v9" />
                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                      </svg>
                      <span>Click to upload (PDF / JPG / PNG)</span>
                    </>
                  )}
                </div>
              </div>

              {certError && (
                <span className="input-error-msg">{certError}</span>
              )}
              {isDeficient && (
                <span className="doc-deficient-label">
                  ⚠ Deficiency — please re-upload
                </span>
              )}
            </div>

            {/* Remove Entry button (when multiple entries) */}
            {gstEntries.length > 1 && !isDeficiencyMode && (
              <button
                type="button"
                className="btn-remove-gst"
                onClick={() => removeGstEntry && removeGstEntry(index)}
                title="Remove this GST entry"
              >
                ✕
              </button>
            )}
          </div>
        );
      })}

      {/* Solid blue pill + Add GST button */}
      {!isDeficiencyMode && (
        <button type="button" className="btn-add-gst" onClick={addGstEntry}>
          <span className="btn-add-gst__plus">+</span> Add GST
        </button>
      )}
    </div>
  );
};

export default GSTEntries;
